import { BadStatusError, cfetch } from '@confluence/network';
import { expValEquals } from '@confluence/feature-experiments';

import type { ResponseContentMetadata } from './DocImportService.ts';

const MAX_PAGE_TITLE_LENGTH = 255;
const TITLE_CONFLICT_STATUSES = new Set([400, 409]);
const TITLE_CONFLICT_MESSAGES = [
	'a page with this title already exists',
	'exists with the same title in this space',
];

export type MarkdownImportErrorCode =
	| 'aborted'
	| 'invalid_destination'
	| 'experiment_disabled'
	| 'http_error'
	| 'outcome_unknown';

export class MarkdownImportError extends Error {
	name = 'MarkdownImportError';

	constructor(
		public readonly code: MarkdownImportErrorCode,
		options: { status?: number; attemptId?: string; cause?: unknown } = {},
	) {
		super(code, options.cause === undefined ? undefined : { cause: options.cause });
		this.status = options.status;
		this.attemptId = options.attemptId;
	}

	readonly status?: number;
	readonly attemptId?: string;
}

type V2PageResponse = {
	id?: string;
	title?: string;
	spaceId?: string;
	subtype?: string;
	_links?: {
		editui?: string;
		edituiv2?: string;
	};
};

export type CreateMarkdownDraftInput = {
	spaceId: string;
	spaceKey: string;
	parentPageId: string | null;
	title: string;
	adf: unknown;
	subtype: 'live';
	attemptId: string;
	signal: AbortSignal;
	onCreateDispatch: () => void;
};

const normalizeResponse = (
	response: V2PageResponse,
	input: CreateMarkdownDraftInput,
): ResponseContentMetadata => {
	if (!response.id || !response.title) {
		throw new Error('The create page response did not contain an ID and title');
	}

	return {
		id: response.id,
		title: response.title,
		subType: response.subtype ?? input.subtype,
		space: {
			id: response.spaceId ?? input.spaceId,
			key: input.spaceKey,
		},
		_links: {
			context: '',
			editui: response._links?.editui ?? response._links?.edituiv2 ?? '',
		},
	};
};

const createTimestampedTitle = (title: string, now = new Date()): string => {
	const timestamp = now.toISOString().slice(0, 19).replace('T', ' ');
	const suffix = ` ${timestamp}`;
	return `${title.slice(0, MAX_PAGE_TITLE_LENGTH - suffix.length)}${suffix}`;
};

const isTitleConflictError = async (error: BadStatusError): Promise<boolean> => {
	if (!TITLE_CONFLICT_STATUSES.has(error.response.status)) {
		return false;
	}

	try {
		const responseBody = (await error.response.clone().text()).toLowerCase();
		return TITLE_CONFLICT_MESSAGES.some((message) => responseBody.includes(message));
	} catch {
		return false;
	}
};

const throwMarkdownImportError = (error: unknown, attemptId: string): never => {
	if (BadStatusError.isBadStatusError(error)) {
		throw new MarkdownImportError('http_error', {
			status: error.response.status,
			cause: error,
		});
	}

	throw new MarkdownImportError('outcome_unknown', {
		attemptId,
		cause: error,
	});
};

export const createMarkdownDraft = async (
	input: CreateMarkdownDraftInput,
): Promise<ResponseContentMetadata> => {
	if (input.signal.aborted) {
		throw new MarkdownImportError('aborted');
	}

	if (!/^\d+$/.test(input.spaceId)) {
		throw new MarkdownImportError('invalid_destination');
	}

	if (!expValEquals('twcg_2627_markdown_file_import_into_confluence', 'isEnabled', true)) {
		throw new MarkdownImportError('experiment_disabled');
	}

	const url = input.parentPageId ? '/wiki/api/v2/pages' : '/wiki/api/v2/pages?root-level=true';
	const createPage = async (title: string): Promise<ResponseContentMetadata> => {
		const response = await cfetch(url, {
			method: 'POST',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				spaceId: input.spaceId,
				...(input.parentPageId ? { parentId: input.parentPageId } : {}),
				status: 'draft',
				subtype: input.subtype,
				title,
				body: {
					representation: 'atlas_doc_format',
					value: JSON.stringify(input.adf),
				},
			}),
			signal: input.signal,
		});
		return normalizeResponse((await response.json()) as V2PageResponse, input);
	};

	input.onCreateDispatch();

	try {
		return await createPage(input.title);
	} catch (error) {
		if (BadStatusError.isBadStatusError(error) && (await isTitleConflictError(error))) {
			try {
				return await createPage(createTimestampedTitle(input.title));
			} catch (retryError) {
				return throwMarkdownImportError(retryError, input.attemptId);
			}
		}

		return throwMarkdownImportError(error, input.attemptId);
	}
};
